import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { useLanguage } from '../contexts/LanguageContext';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { UserCheck, Plus, Edit, Search, KeyRound, User, ImageIcon } from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

export const Clients = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [ordersDialogOpen, setOrdersDialogOpen] = useState(false);
  const [clientOrders, setClientOrders] = useState([]);
  const [selectedClientName, setSelectedClientName] = useState('');
  const [activateDialog, setActivateDialog] = useState(null);
  const [activatePassword, setActivatePassword] = useState('');
  const [activateWhatsapp, setActivateWhatsapp] = useState('');
  const [activateLoading, setActivateLoading] = useState(false);
  const [transportCountries, setTransportCountries] = useState([]);
  const [clientActivateDialog, setClientActivateDialog] = useState(null);
  const [clientActivatePassword, setClientActivatePassword] = useState('');
  const [clientActivateLoading, setClientActivateLoading] = useState(false);
  const logoInputRef = React.useRef(null);
  const [logoUploadClientId, setLogoUploadClientId] = useState(null);
  const [formData, setFormData] = useState({
    company_name: '', name: '', firstname: '', address: '', postal_code: '', city: '', country: '', phone: '', email: ''
  });

  useEffect(() => {
    fetchClients();
  }, [search]);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/transport/countries`, { withCredentials: true })
      .then(res => setTransportCountries(res.data || []))
      .catch(() => {});
  }, []);

  const fetchClients = async () => {
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : '';
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/clients${params}`,
        { withCredentials: true }
      );
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingClient) {
        await axios.put(
          `${process.env.REACT_APP_BACKEND_URL}/api/clients/${editingClient.id}`,
          formData,
          { withCredentials: true }
        );
        toast.success('Client modifié avec succès');
      } else {
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/clients`,
          formData,
          { withCredentials: true }
        );
        toast.success('Client ajouté avec succès');
      }
      setDialogOpen(false);
      resetForm();
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      company_name: client.company_name || '',
      name: client.name || '',
      firstname: client.firstname || '',
      address: client.address || '',
      postal_code: client.postal_code || '',
      city: client.city || '',
      country: client.country || '',
      phone: client.phone || '',
      email: client.email || ''
    });
    setDialogOpen(true);
  };

  const handleClientClick = async (client) => {
    setSelectedClientName(`${client.name} ${client.firstname}`);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/clients/${client.id}/orders`,
        { withCredentials: true }
      );
      setClientOrders(response.data);
      setOrdersDialogOpen(true);
    } catch (error) {
      console.error('Error fetching client orders:', error);
    }
  };

  const openActivateDialog = (client) => {
    setActivateDialog(client);
    setActivatePassword('');
    setActivateWhatsapp(client.whatsapp_link || '');
  };

  const handleTogglePremium = async (clientId, newValue) => {
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/clients/${clientId}/premium`, { premium: newValue }, { withCredentials: true });
      toast.success(newValue ? 'Client passé en Premium' : 'Premium désactivé');
      fetchClients();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleActivate = async () => {
    if (!activateDialog) return;
    setActivateLoading(true);
    try {
      const payload = { portal_type: 'revendeur' };
      if (activatePassword) payload.password = activatePassword;
      if (activateWhatsapp !== undefined) payload.whatsapp_link = activateWhatsapp;
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/clients/${activateDialog.id}/activate`,
        payload,
        { withCredentials: true }
      );
      toast.success(`Espace revendeur ${activatePassword ? 'activé' : 'mis à jour'} pour ${activateDialog.name}`);
      setActivateDialog(null);
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally { setActivateLoading(false); }
  };

  const handleClientActivate = async () => {
    if (!clientActivateDialog || !clientActivatePassword) return;
    setClientActivateLoading(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/clients/${clientActivateDialog.id}/activate`,
        { password: clientActivatePassword, portal_type: 'client' },
        { withCredentials: true }
      );
      toast.success(`Espace client activé pour ${clientActivateDialog.name}`);
      setClientActivateDialog(null);
      setClientActivatePassword('');
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally { setClientActivateLoading(false); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !logoUploadClientId) return;
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/clients/${logoUploadClientId}/upload-logo`, formDataUpload, { withCredentials: true });
      toast.success('Logo téléchargé');
      fetchClients();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur upload'); }
    finally { e.target.value = ''; setLogoUploadClientId(null); }
  };

  const [passwordPrompt, setPasswordPrompt] = useState(null); // {clientId, accessType}
  const [promptPassword, setPromptPassword] = useState('');

  const handleToggleAccess = async (clientId, accessType, hasPassword) => {
    if (!hasPassword) {
      // Need to set a password first
      setPasswordPrompt({ clientId, accessType });
      setPromptPassword('');
      return;
    }
    try {
      const res = await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/clients/${clientId}/toggle-access`,
        { access_type: accessType }, { withCredentials: true });
      toast.success(res.data.message);
      fetchClients();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handlePasswordPromptSubmit = async () => {
    if (!passwordPrompt || !promptPassword) return;
    try {
      const res = await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/clients/${passwordPrompt.clientId}/toggle-access`,
        { access_type: passwordPrompt.accessType, password: promptPassword }, { withCredentials: true });
      toast.success(res.data.message);
      setPasswordPrompt(null);
      setPromptPassword('');
      fetchClients();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };


  const resetForm = () => {
    setEditingClient(null);
    setFormData({ company_name: '', name: '', firstname: '', address: '', postal_code: '', city: '', country: '', phone: '', email: '' });
  };

  const getStatusBadge = (status) => {
    const styles = {
      'commandée': 'bg-blue-100 text-blue-800 border-blue-200',
      'impression': 'bg-purple-100 text-purple-800 border-purple-200',
      'couture': 'bg-indigo-100 text-indigo-800 border-indigo-200',
      'livraison bureau': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'en cours de livraison client': 'bg-orange-100 text-orange-800 border-orange-200',
      'livrée': 'bg-emerald-100 text-emerald-800 border-emerald-200'
    };
    return styles[status] || 'bg-slate-100 text-slate-800 border-slate-200';
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">Clients</h1>
            <p className="text-sm text-slate-600 mt-1">Gestion des clients</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="create-client-button" className="bg-slate-900 hover:bg-slate-800">
                <Plus size={18} className="mr-2" />
                Nouveau client
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{editingClient ? 'Modifier le client' : 'Nouveau client'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Nom entreprise *</Label>
                  <Input data-testid="client-company-input" value={formData.company_name} onChange={(e) => setFormData({ ...formData, company_name: e.target.value })} placeholder="Nom de l'entreprise" required />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nom*</Label>
                    <Input data-testid="client-name-input" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
                  </div>
                  <div className="space-y-2">
                    <Label>Prénom</Label>
                    <Input data-testid="client-firstname-input" value={formData.firstname} onChange={(e) => setFormData({ ...formData, firstname: e.target.value })} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Adresse</Label>
                  <Input data-testid="client-address-input" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Code postal</Label>
                    <Input data-testid="client-postal-input" value={formData.postal_code} onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>Ville</Label>
                    <Input data-testid="client-city-input" value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Pays</Label>
                    <Select value={formData.country} onValueChange={(val) => setFormData({ ...formData, country: val })}>
                      <SelectTrigger data-testid="client-country-input">
                        <SelectValue placeholder="Sélectionner un pays" />
                      </SelectTrigger>
                      <SelectContent>
                        {transportCountries.map(c => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Téléphone</Label>
                    <Input data-testid="client-phone-input" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input data-testid="client-email-input" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>Annuler</Button>
                  <Button data-testid="submit-client-button" type="submit" className="bg-slate-900 hover:bg-slate-800">{editingClient ? 'Modifier' : 'Créer'}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <UserCheck size={20} className="text-slate-600" />
                <CardTitle className="text-lg font-medium">{clients.length} client(s)</CardTitle>
              </div>
              <div className="relative w-64">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  data-testid="client-search-input"
                  placeholder="Rechercher par nom ou entreprise..."
                  className="pl-9"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto"></div>
              </div>
            ) : clients.length === 0 ? (
              <p className="text-center py-8 text-sm text-slate-500">Aucun client</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-medium">Entreprise</TableHead>
                      <TableHead className="font-medium">Nom</TableHead>
                      <TableHead className="font-medium">Email</TableHead>
                      <TableHead className="font-medium">Ville</TableHead>
                      <TableHead className="font-medium">Pays</TableHead>
                      <TableHead className="font-medium">Téléphone</TableHead>
                      <TableHead className="font-medium text-right">Commandes</TableHead>
                      <TableHead className="font-medium text-right">CA total</TableHead>
                      <TableHead className="font-medium text-center">Client</TableHead>
                      <TableHead className="font-medium text-center">Revendeur</TableHead>
                      <TableHead className="font-medium text-center">Premium</TableHead>
                      <TableHead className="font-medium">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {clients.map((client) => (
                      <TableRow key={client.id} data-testid="client-row" className="hover:bg-slate-50">
                        <TableCell className="font-medium">{client.company_name || '-'}</TableCell>
                        <TableCell
                          className="cursor-pointer text-blue-600 hover:underline"
                          onClick={() => handleClientClick(client)}
                        >
                          {client.name} {client.firstname}
                        </TableCell>
                        <TableCell className="text-sm">{client.email || '-'}</TableCell>
                        <TableCell className="text-sm">{client.city || '-'}</TableCell>
                        <TableCell className="text-sm">{client.country || '-'}</TableCell>
                        <TableCell className="text-sm">{client.phone || '-'}</TableCell>
                        <TableCell className="text-right font-medium">{client.order_count}</TableCell>
                        <TableCell className="text-right font-medium">{client.total_sales?.toLocaleString('fr-FR')} &euro;</TableCell>
                        <TableCell className="text-center">
                          <Switch data-testid={`toggle-client-${client.id}`} checked={!!client.access_client}
                            onCheckedChange={() => handleToggleAccess(client.id, 'client', !!client.has_revendeur_access)} />
                        </TableCell>
                        <TableCell className="text-center">
                          <Switch data-testid={`toggle-revendeur-${client.id}`} checked={!!client.access_revendeur}
                            onCheckedChange={() => handleToggleAccess(client.id, 'revendeur', !!client.has_revendeur_access)} />
                        </TableCell>
                        <TableCell className="text-center">
                          <Switch data-testid={`toggle-premium-${client.id}`} checked={!!client.premium}
                            onCheckedChange={() => handleTogglePremium(client.id, !client.premium)} />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button data-testid="edit-client-button" size="sm" variant="ghost" onClick={() => handleEdit(client)} className="h-8 w-8 p-0">
                              <Edit size={16} />
                            </Button>
                            <Button size="sm" variant="ghost"
                              onClick={() => { setLogoUploadClientId(client.id); logoInputRef.current?.click(); }}
                              className={`h-8 w-8 p-0 ${client.logo_url ? 'text-violet-600' : 'text-slate-400'}`}
                              title={client.logo_url ? 'Changer le logo' : 'Ajouter un logo'}>
                              <ImageIcon size={16} />
                            </Button>
                            <Button data-testid={`activate-revendeur-${client.id}`} size="sm" variant="ghost" onClick={() => openActivateDialog(client)}
                              className="h-8 w-8 p-0 text-slate-400" title="Mot de passe / WhatsApp">
                              <KeyRound size={16} />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        <Dialog open={ordersDialogOpen} onOpenChange={setOrdersDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Commandes de {selectedClientName}</DialogTitle>
            </DialogHeader>
            {clientOrders.length === 0 ? (
              <p className="text-sm text-slate-500 py-4">Aucune commande</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-medium">N&deg; Commande</TableHead>
                    <TableHead className="font-medium">Date</TableHead>
                    <TableHead className="font-medium">Pi&egrave;ces</TableHead>
                    <TableHead className="font-medium">Statut</TableHead>
                    <TableHead className="font-medium text-right">Prix</TableHead>
                    <TableHead className="font-medium">Soci&eacute;t&eacute;</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {clientOrders.map((order) => (
                    <TableRow key={order.id} data-testid="client-order-row" className="hover:bg-slate-50">
                      <TableCell className="font-medium">{order.order_number}</TableCell>
                      <TableCell className="text-sm">{new Date(order.order_date).toLocaleDateString('fr-FR')}</TableCell>
                      <TableCell>{order.num_pieces} {order.piece_type}</TableCell>
                      <TableCell>
                        <Badge className={`${getStatusBadge(order.status)} border text-xs`}>{order.status}</Badge>
                      </TableCell>
                      <TableCell className="text-right font-medium">{order.sale_price_eur?.toFixed(2)} &euro;</TableCell>
                      <TableCell className="text-sm">{order.company}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </DialogContent>
        </Dialog>

        {/* Activate Revendeur Dialog */}
        <Dialog open={!!activateDialog} onOpenChange={() => setActivateDialog(null)}>
          <DialogContent data-testid="activate-revendeur-dialog" className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <KeyRound size={18} className="text-emerald-600" />
                Espace Revendeur — {activateDialog?.name} {activateDialog?.firstname}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {activateDialog?.has_revendeur_access && (
                <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200">Accès revendeur activé</Badge>
              )}
              <div>
                <Label className="text-sm font-medium">Email de connexion</Label>
                <p className="text-sm text-slate-700 bg-slate-50 rounded px-3 py-2 mt-1">{activateDialog?.email || 'Pas d\'email'}</p>
              </div>
              <div>
                <Label className="text-sm font-medium">{activateDialog?.has_revendeur_access ? 'Nouveau mot de passe (laisser vide pour ne pas changer)' : 'Mot de passe'}</Label>
                <Input data-testid="activate-password-input" type="password" value={activatePassword} onChange={e => setActivatePassword(e.target.value)}
                  placeholder={activateDialog?.has_revendeur_access ? '••••••••' : 'Mot de passe du revendeur'} className="mt-1" />
              </div>
              <div>
                <Label className="text-sm font-medium">Lien WhatsApp du groupe</Label>
                <Input data-testid="activate-whatsapp-input" value={activateWhatsapp} onChange={e => setActivateWhatsapp(e.target.value)}
                  placeholder="https://chat.whatsapp.com/..." className="mt-1" />
                <p className="text-xs text-slate-500 mt-1">Si vide, la rubrique WhatsApp n'apparaîtra pas sur le dashboard revendeur</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-600">
                <p className="font-medium mb-1">URL de connexion revendeur :</p>
                <code className="text-blue-700">{window.location.origin}/revendeur/login</code>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setActivateDialog(null)}>Annuler</Button>
                <Button data-testid="activate-confirm-btn" className="bg-emerald-600 hover:bg-emerald-700" onClick={handleActivate} disabled={activateLoading || (!activatePassword && !activateDialog?.has_revendeur_access)}>
                  {activateLoading ? '...' : activateDialog?.has_revendeur_access ? 'Mettre à jour' : 'Activer'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        {/* Hidden logo upload input */}
        <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />

        {/* Activate Client Dialog */}
        <Dialog open={!!clientActivateDialog} onOpenChange={() => setClientActivateDialog(null)}>
          <DialogContent data-testid="activate-client-dialog" className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <User size={18} className="text-blue-600" />
                Espace Client — {clientActivateDialog?.name} {clientActivateDialog?.firstname}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {clientActivateDialog?.has_revendeur_access && (
                <Badge className="bg-blue-100 text-blue-800 border border-blue-200">Accès client activé</Badge>
              )}
              <div>
                <Label className="text-sm font-medium">Email de connexion</Label>
                <p className="text-sm text-slate-700 bg-slate-50 rounded px-3 py-2 mt-1">{clientActivateDialog?.email || "Pas d'email"}</p>
              </div>
              <div>
                <Label className="text-sm font-medium">{clientActivateDialog?.has_revendeur_access ? 'Nouveau mot de passe (laisser vide pour ne pas changer)' : 'Mot de passe'}</Label>
                <Input data-testid="activate-client-password-input" type="password" value={clientActivatePassword} onChange={e => setClientActivatePassword(e.target.value)}
                  placeholder="Mot de passe du client" className="mt-1" />
              </div>
              <div className="bg-blue-50 rounded-lg p-3 text-xs text-slate-600">
                <p className="font-medium mb-1">URL de connexion client :</p>
                <code className="text-blue-700">{window.location.origin}/espace-client</code>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setClientActivateDialog(null)}>Annuler</Button>
                <Button data-testid="activate-client-confirm-btn" className="bg-blue-600 hover:bg-blue-700" onClick={handleClientActivate}
                  disabled={clientActivateLoading || (!clientActivatePassword && !clientActivateDialog?.has_revendeur_access)}>
                  {clientActivateLoading ? '...' : clientActivateDialog?.has_revendeur_access ? 'Mettre à jour' : 'Activer'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        {/* Password Prompt Dialog (first activation) */}
        <Dialog open={!!passwordPrompt} onOpenChange={() => setPasswordPrompt(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Mot de passe requis</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-slate-600">Ce client n'a pas encore de mot de passe. Définissez-en un pour activer l'accès <strong>{passwordPrompt?.accessType === 'client' ? 'Client' : 'Revendeur'}</strong>.</p>
              <div>
                <Label className="text-sm font-medium">Mot de passe</Label>
                <Input type="password" value={promptPassword} onChange={e => setPromptPassword(e.target.value)} placeholder="Mot de passe" className="mt-1" />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setPasswordPrompt(null)}>Annuler</Button>
                <Button className="bg-slate-900 hover:bg-slate-800" onClick={handlePasswordPromptSubmit} disabled={!promptPassword}>Activer</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};
